import {
  Avatar,
  Box,
  MenuItem,
  PaperProps,
  Select,
  SelectProps,
  Typography,
  styled,
} from '@mui/material'
import { colorPalette } from '../../../style/colorPalette'
import { intl } from '../../../../i18n'
import TicketListApi, {
  TicketListDetail,
} from '../../../../lib/functions/ticketList'
import { useProjectPrivateContext } from '../../../context/projectContext'
import { projectPrivate } from '../../../higher-order-components/projectPrivate'
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'
import { selectTicketListsForDisplay } from '../../../../domain/entity/TicketListEntity'
import { useStableFooter } from '../../../hooks/useStableFooter'
import { TicketListOption } from '../../../features/TicketListOption'
import { TicketListShowClosedToggle } from '../../../features/TicketListShowClosedToggle'

// API境界。WbsItemDetail.status は string 型だが、バックエンドは WbsItemStatus を返すため、
// response.json を受ける時点で一度だけ寄せ、以降はキャストせずに扱う。
type TicketList = TicketListDetail & {
  wbsItem: TicketListDetail['wbsItem'] & { status: WbsItemStatus }
}

type Props = {
  ticketType?: string
  onChangeTicketType?: (value?: string) => void
  ticketListUuid: string | undefined
  onChangeTicketList: (value: TicketListDetail | undefined) => void
}

type StyledSelectProps = Omit<SelectProps, 'sx' | 'MenuProps'> & {
  children: ReactNode
  // 選択肢の外側、メニューの末尾に置く要素。listbox の子にすると選択肢として扱われてしまうため、
  // 単票画面の Autocomplete の listFooter と同じく Paper の末尾に置く。
  menuFooter?: ReactNode
}

const StyledSelect = ({
  children,
  menuFooter,
  ...props
}: StyledSelectProps) => {
  const { footerRef: menuFooterRef, hasFooter: hasMenuFooter } =
    useStableFooter(menuFooter)
  const MenuPaper = useMemo(
    () =>
      hasMenuFooter
        ? forwardRef<HTMLDivElement, PaperProps>(function MenuPaper(
            { children: paperChildren, ...paperProps },
            ref
          ) {
            return (
              <div {...paperProps} ref={ref}>
                {paperChildren}
                {menuFooterRef.current}
              </div>
            )
          })
        : undefined,
    [hasMenuFooter, menuFooterRef]
  )
  return (
    <Select
      {...props}
      sx={{
        border: `1px solid ${colorPalette.monotone[2]}`,
        borderRadius: '4px',
        width: '370px',
        height: '40px',
        color: colorPalette.monotone[4],
        backgroundColor: colorPalette.monotone[0],
      }}
      MenuProps={{
        sx: {
          width: '370px',
          height: '540px',
          fontSize: '14px',
          fontWeight: 700,
          lineHeight: '21px',
        },
        PaperProps: { component: MenuPaper },
      }}
    >
      {children}
    </Select>
  )
}

export const TicketListSelector = projectPrivate(
  ({
    ticketType,
    onChangeTicketType,
    ticketListUuid,
    onChangeTicketList,
  }: Props) => {
    const { projectUuid, ticketTypes } = useProjectPrivateContext()
    const [ticketLists, setTicketLists] = useState<TicketList[]>([])
    const [showsClosed, setShowsClosed] = useState<boolean>(false)

    const fetchTicketLists = useCallback(async () => {
      let lists: TicketList[] = []
      if (projectUuid && ticketType) {
        const response = await TicketListApi.getAll({
          ticketType: ticketType,
        })
        lists = response.json as TicketList[]
      }
      setTicketLists(lists)
      const list = lists.find(v => v.uuid === ticketListUuid)
      onChangeTicketList(list)
    }, [projectUuid, ticketType, ticketListUuid, onChangeTicketList])

    // APIの並び順は status の辞書順(DISCARD→DOING→DONE→REVIEW→TODO)になっているため、
    // 業務上の進行順に並べ直す。
    // Done・Discardは既定で隠すが、選択中のものを消すとSelectの値が選択肢から外れるため残す。
    // 出し分けと並び順は単票画面と同じ規則にするため、ドメイン層の関数を通す。
    const visibleTicketLists = useMemo(
      () =>
        selectTicketListsForDisplay(
          ticketLists,
          ticketList => ({
            uuid: ticketList.uuid,
            status: ticketList.wbsItem.status,
            name: ticketList.wbsItem.displayName,
          }),
          { showsClosed, selectedUuid: ticketListUuid }
        ),
      [ticketLists, showsClosed, ticketListUuid]
    )

    const renderTicketListValue = useCallback(
      (value: unknown) => {
        const selected = ticketLists.find(v => v.uuid === value)
        if (!selected) return ''
        return (
          <SelectedTicketList>
            <TicketListOption
              displayName={selected.wbsItem.displayName}
              status={selected.wbsItem.status}
            />
          </SelectedTicketList>
        )
      },
      [ticketLists]
    )

    const onChangeTicketListUuid = useCallback(
      event => {
        const targetUuid = event.target.value
        const targetDetail = ticketLists.find(v => v.uuid === targetUuid)
        onChangeTicketList(targetDetail)
      },
      [ticketLists, onChangeTicketList]
    )

    useEffect(() => {
      fetchTicketLists()
    }, [projectUuid, ticketType, ticketListUuid])

    return (
      <Box
        sx={{
          margin: '10px 10px 0',
          padding: '10px',
          backgroundColor: '#D8DDE54D',
          display: 'flex',
          flexFlow: 'row',
          gap: '8px',
        }}
      >
        {onChangeTicketType && (
          <Box sx={{ display: 'flex', flexFlow: 'column' }}>
            <Typography
              sx={{ minWidth: '128px', color: colorPalette.monotone[4] }}
            >
              {intl.formatMessage({ id: 'ticketType' })}
            </Typography>
            <StyledSelect
              value={ticketType || ''}
              onChange={e => onChangeTicketType(e.target.value as string)}
              renderValue={value => {
                const selected = ticketTypes.find(v => v.code === value)
                if (!selected) return ''
                return <TicketTypeDisplay ticketTypeVO={selected} />
              }}
            >
              {ticketTypes.map(v => (
                <MenuItem key={v.code} value={v.code}>
                  <TicketTypeDisplay ticketTypeVO={v} isMenuItem={true} />
                </MenuItem>
              ))}
            </StyledSelect>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexFlow: 'column' }}>
          <Typography
            sx={{ minWidth: '128px', color: colorPalette.monotone[4] }}
          >
            {intl.formatMessage({ id: 'ticketList' })}
          </Typography>
          <StyledSelect
            value={ticketListUuid || ''}
            onChange={onChangeTicketListUuid}
            renderValue={renderTicketListValue}
            menuFooter={
              <TicketListShowClosedToggle
                checked={showsClosed}
                onChange={setShowsClosed}
              />
            }
          >
            {visibleTicketLists.map(v => (
              <TicketListMenuItem key={v.uuid} value={v.uuid}>
                <TicketListOption
                  displayName={v.wbsItem.displayName}
                  status={v.wbsItem.status}
                />
              </TicketListMenuItem>
            ))}
          </StyledSelect>
        </Box>
      </Box>
    )
  }
)

// この画面の従来通りの太さを保つ。TicketListOption は単票画面と共用で、そちらは 400 のため、
// 名前を描画している要素をここで上書きする。開いた選択肢と入力欄で太さが変わらないよう、
// メニュー側と入力欄側の双方に当てる。
const boldTicketListName = {
  '& span': {
    fontWeight: 500,
  },
}
const TicketListMenuItem = styled(MenuItem)({
  color: colorPalette.monotone[4],
  lineHeight: '21px',
  ...boldTicketListName,
})
const SelectedTicketList = styled('span')(boldTicketListName)

const TicketTypeDisplay = ({
  ticketTypeVO,
  isMenuItem = false,
}: {
  ticketTypeVO: WbsItemTypeVO
  isMenuItem?: boolean
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <Avatar
      variant={'circular'}
      src={ticketTypeVO.iconUrl}
      sx={{ width: '20px', height: '20px' }}
    />
    <Typography
      sx={{
        color: isMenuItem ? colorPalette.monotone[4] : undefined,
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '20px',
      }}
    >
      {ticketTypeVO.name}
    </Typography>
  </Box>
)
